import type { AccountInfo } from "@azure/msal-browser";
import { msalInstance, MSAL_POLICY_SIGNIN, MSAL_POLICY_SIGNUP } from "@msal/msalConfig";
import {useAuthStore} from "@store/AuthStore";
import {useAppContextStore} from "@store/AppContextStore";

type PolicyKind = "signin" | "signup" | "unknown";

function normalize(s: any): string {
    return String(s ?? "").trim();
}

function getPolicyFromAccount(a: AccountInfo): string {
    const c: any = a.idTokenClaims || {};
    return normalize(c.tfp || c.acr);
}

function classifyPolicy(policy: string): PolicyKind {
    const p = policy.toLowerCase();
    if (p.includes(MSAL_POLICY_SIGNIN.toLowerCase())) return "signin";
    if (p.includes(MSAL_POLICY_SIGNUP.toLowerCase())) return "signup";
    return "unknown";
}

function getAuthTime(a: AccountInfo): number {
    const c: any = a.idTokenClaims || {};
    return Number(c.auth_time ?? c.iat ?? 0);
}

export function pickBestAccount(preferred: PolicyKind = "signin"): AccountInfo | null {
    const active = msalInstance.getActiveAccount();
    const all = msalInstance.getAllAccounts();

    console.log("[MSAL][pickBestAccount] accounts in cache:", all.length);

    const matchesPreferred = (a: AccountInfo) => {
        const pol = getPolicyFromAccount(a);
        const kind = classifyPolicy(pol);
        return kind === preferred;
    };

    if (active) {
        const pol = getPolicyFromAccount(active);
        const kind = classifyPolicy(pol);
        console.log("[MSAL][pickBestAccount] activeAccount:", active.homeAccountId, "policy:", pol || "n/a", "kind:", kind);

        if (preferred === "unknown" || kind === preferred) {
            console.log("[MSAL][pickBestAccount] using activeAccount (preferred match)");
            return active;
        }
    }

    if (!all.length) return null;
    if (all.length === 1) {
        console.log("[MSAL][pickBestAccount] single account -> using it");
        return all[0];
    }

    const filtered = preferred === "unknown" ? all : all.filter(matchesPreferred);
    const pool = filtered.length ? filtered : all;

    const best = [...pool].sort((a, b) => getAuthTime(b) - getAuthTime(a))[0] ?? null;

    if (best) {
        const pol = getPolicyFromAccount(best);
        console.log("[MSAL][pickBestAccount] selected:", best.homeAccountId, "policy:", pol || "n/a");
    }

    return best;
}

export async function cleanupRejectedMsalAccount() {
    const active = msalInstance.getActiveAccount();

    console.log("[MSAL][businessDenyCleanup] activeAccount:", active?.homeAccountId);

    try {
        if (active) {
            await msalInstance.clearCache({ account: active });
        } else {
            await msalInstance.clearCache();
        }
    } catch (e) {
        console.warn("[MSAL][businessDenyCleanup] clearCache failed", e);
    }

    try {
        msalInstance.setActiveAccount(null);
    } catch (e) {
        console.warn("[MSAL][businessDenyCleanup] setActiveAccount(null) failed", e);
    }

        useAuthStore.getState().reset();
        useAppContextStore.getState().reset();

        sessionStorage.removeItem("auth:returnUrl");
}